import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import UploadField from '@/components/UI/UploadField';
import Breadcrumb from '@/components/common/Breadcrumb';
import MediaInputField from '@/components/common/media/MediaInputField';
import MediaPreview from '@/components/common/media/MediaPreview';
import { IMediaProp } from '@/interfaces/mediaResponse.interface';
import { RoutesLink } from '@/utility/RouteLinks';
import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import ErrorMessage from '../../components/common/ErrorMessage';
import BtnLoader from '@/components/UI/loaders/BtnLoader';
import ProductDragDrop from '@/components/product/ProductDragDrop';

interface IBrand {
  name: string;
  description: string;
  status: 'active' | 'inactive';
  file: File | null;
}

const EditBrand = () => {
  const navigate = useNavigate();
  const [mediaFile, setMediaFile] = useState<IMediaProp | null>(null);
  const { id } = useParams();
  const brandId = id;
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<IBrand>({
    defaultValues: {
      name: '',
      description: '',
      status: 'active',
      file: null,
    },
  });

  const onSubmit = async (data: IBrand) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('description', data.description);
      formData.append('status', data.status);
      if (data.file) {
        formData.append('file', data.file);
      }
      let PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}`;
      if (brandId) {
        PATH = `${PATH}/${brandId}`;
      }
      await callApi(PATH, {
        method: brandId ? 'PATCH' : 'POST',
        multipart: true,
        data: brandId ? data : formData,
      });
      setIsLoading(false);
      navigate(RoutesLink.brandsList);
    } catch (error) {
      setIsLoading(false);
      ThrowError(error);
    }
  };

  const handleDeleteMedia = async (id?: string) => {
    if (id) {
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.media}/${id}`;
      const res = await callApi(PATH, { method: 'DELETE' });
      if (res) {
        setMediaFile(null);
      }
    } else {
      setMediaFile(null);
    }
  };

  const fetchBrandInfo = async () => {
    try {
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}/${brandId}`;
      const res: any = await callApi(PATH, { method: 'GET' });
      reset({
        name: res.name,
        description: res.description,
        status: res.status,
        file: null,
      });

      if (res.media && res.media.length > 0) {
        setMediaFile({
          id: res.media[0].id,
          url: brandId
            ? res.media[0].url
            : `${API_URL_PRODUCT_SERVICE}/${res.media[0].url}`,
          type: res.media[0].type,
          altText: res.media[0].altText || 'Brand Logo',
        });
      }
    } catch (err) {
      ThrowError(err);
    }
  };

  useEffect(() => {
    brandId && fetchBrandInfo();
  }, [brandId, reset]);

  const breadcrumb = [
    { label: 'Brands', href: RoutesLink.brandsList },
    { label: 'Edit Brand' },
  ];

  return (
    <Layout>
      <Breadcrumb items={breadcrumb} />
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <div className="space-y-5">
            {/* About Brand */}
            <h2 className="text-lg font-semibold mb-4">About Brand</h2>
            <div>
              <FormField
                label="Name"
                type="text"
                placeholder="Enter brand name"
                {...register('name')}
              />
            </div>

            <div>
              <FormField
                type="textarea"
                label="Description"
                placeholder="Start describing the brand..."
                rows={4}
                {...register('description', {
                  required: 'Description is required',
                })}
              />
              <ErrorMessage message={errors.description?.message} />
            </div>

            <div>
              <h4 className="block text-sm font-medium text-gray-700 mb-2">
                Upload Brand Logo
              </h4>

              {brandId ? (
                <UploadField
                  referenceType="brand"
                  referenceId={brandId}
                  multiple={false}
                  initialMedia={mediaFile ? [mediaFile] : []}
                />
              ) : (
                <Controller
                  name="file"
                  control={control}
                  render={({ field }) => (
                    <MediaInputField
                      helperText="Upload brand logo"
                      multiple={false}
                      onFilesChange={(files) => {
                        if (files && files.length > 0) {
                          const file = files[0];
                          field.onChange(file);
                          setMediaFile({
                            url: URL.createObjectURL(file),
                            type: file.type.startsWith('video/')
                              ? 'video'
                              : 'image',
                            altText: file.name,
                          });
                        }
                      }}
                    />
                  )}
                />
              )}
              {mediaFile && !brandId && (
                <div className="mt-4">
                  <MediaPreview
                    mediaUrls={[mediaFile]}
                    handleDeleteMedia={handleDeleteMedia}
                  />
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mb-5">
          <button
            onClick={() => navigate(-1)}
            type="button"
            className="btn-secondary"
          >
            Cancel
          </button>
          <button disabled={isLoading} type="submit" className="btn-primary">
            {isLoading && <BtnLoader />} Save
          </button>
        </div>
      </form>
      <ProductDragDrop brandId={brandId as string} />
    </Layout>
  );
};

export default EditBrand;
